import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  Download,
  FileText,
  FileSpreadsheet,
  FileImage,
  FileVideo,
  FileArchive,
  File,
  Loader2,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';

interface FilePreviewDialogProps {
  file: {
    id: string;
    file_name: string;
    file_url: string;
    mime_type: string;
    size_bytes: number;
    category?: string;
    subfolder?: string;
    visibility?: string;
    created_at: string;
    summary?: any;
  } | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bucketName?: string;
}

export function FilePreviewDialog({
  file,
  open,
  onOpenChange,
  bucketName = 'data-room',
}: FilePreviewDialogProps) {
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (file && open) {
      loadSignedUrl();
    }
  }, [file, open]);

  const loadSignedUrl = async () => {
    if (!file) return;

    setLoading(true);
    try {
      const { data, error } = await supabase.storage
        .from(bucketName)
        .createSignedUrl(file.file_url, 3600); // 1 hour

      if (error) throw error;
      setSignedUrl(data.signedUrl);
    } catch (error) {
      console.error('Error generating signed URL:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!signedUrl || !file) return;

    try {
      const response = await fetch(signedUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.file_name;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  const getFileIcon = (mimeType: string) => {
    if (mimeType.startsWith('image/')) {
      return <FileImage className="h-5 w-5" />;
    }
    if (mimeType === 'application/pdf' || mimeType.includes('document')) {
      return <FileText className="h-5 w-5" />;
    }
    if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) {
      return <FileSpreadsheet className="h-5 w-5" />;
    }
    if (mimeType.startsWith('video/')) {
      return <FileVideo className="h-5 w-5" />;
    }
    if (mimeType.includes('zip') || mimeType.includes('archive')) {
      return <FileArchive className="h-5 w-5" />;
    }
    return <File className="h-5 w-5" />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const renderPreview = () => {
    if (!file || !signedUrl) return null;

    if (loading) {
      return (
        <div className="flex items-center justify-center h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      );
    }

    // Image preview
    if (file.mime_type.startsWith('image/')) {
      return (
        <div className="flex items-center justify-center bg-muted/20 rounded-lg p-4">
          <img
            src={signedUrl}
            alt={file.file_name}
            className="max-w-full max-h-[500px] object-contain rounded"
          />
        </div>
      );
    }

    // PDF preview
    if (file.mime_type === 'application/pdf') {
      return (
        <iframe
          src={signedUrl}
          className="w-full h-[500px] rounded-lg border"
          title={file.file_name}
        />
      );
    }

    // Default: Show file icon and metadata
    return (
      <div className="flex flex-col items-center justify-center h-[400px] gap-4 bg-muted/20 rounded-lg">
        <div className="rounded-full bg-background p-6">{getFileIcon(file.mime_type)}</div>
        <div className="text-center">
          <p className="font-medium">{file.file_name}</p>
          <p className="text-sm text-muted-foreground mt-1">
            Preview not available for this file type
          </p>
          <Button onClick={handleDownload} className="mt-4" size="sm">
            <Download className="h-4 w-4 mr-2" />
            Download File
          </Button>
        </div>
      </div>
    );
  };

  if (!file) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {getFileIcon(file.mime_type)}
            {file.file_name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {renderPreview()}

          <Separator />

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">File Size</p>
              <p className="font-medium">{formatFileSize(file.size_bytes)}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Type</p>
              <p className="font-medium">{file.mime_type}</p>
            </div>
            {file.category && (
              <div>
                <p className="text-muted-foreground">Category</p>
                <Badge variant="outline">{file.category}</Badge>
              </div>
            )}
            {file.subfolder && (
              <div>
                <p className="text-muted-foreground">Subfolder</p>
                <Badge variant="outline">{file.subfolder}</Badge>
              </div>
            )}
            {file.visibility && (
              <div>
                <p className="text-muted-foreground">Visibility</p>
                <Badge variant="secondary">{file.visibility}</Badge>
              </div>
            )}
            <div>
              <p className="text-muted-foreground">Uploaded</p>
              <p className="font-medium">{format(new Date(file.created_at), 'PPp')}</p>
            </div>
          </div>

          {file.summary && (
            <>
              <Separator />
              <div>
                <p className="text-sm font-medium mb-2">AI Summary</p>
                <p className="text-sm text-muted-foreground">{file.summary}</p>
              </div>
            </>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Close
            </Button>
            <Button onClick={handleDownload}>
              <Download className="h-4 w-4 mr-2" />
              Download
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
